import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { useParams } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useCRM } from './CRMContext';
import { resolveEmployeeDesignations } from './contractorPayablesAccess';
import { getAssignedDesigner } from './dealDesignerArchitect';
import { db } from './firebase';
import { firestoreSnapshotListenError } from './firestoreListenDebug';
import {
  designersToJoineryPeople,
  staffToJoineryPeople,
  type JoineryPerson,
} from './proposalJoineryPeople';
import { canViewTeamCompensation } from './teamCompensationAccess';
import { useInstallLaborPeople } from './useInstallLaborPeople';

function compensationRate(data: Record<string, unknown> | undefined): number | undefined {
  const n = Number(data?.designerHourlyRate);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

export function useJoineryPeople(): { staff: JoineryPerson[]; designers: JoineryPerson[] } {
  const { user } = useAuth();
  const { crmUsers } = useCRM();
  const installPeople = useInstallLaborPeople();
  const designations = resolveEmployeeDesignations(user, crmUsers);
  const briefingProfile = crmUsers.find((row) => row.uid === user?.id)?.executiveBriefing?.profile ?? null;
  const canViewRates = canViewTeamCompensation({
    authRole: user?.role,
    designations,
    briefingProfile,
  });
  const [designerRates, setDesignerRates] = useState<Record<string, number>>({});

  useEffect(() => {
    if (!canViewRates) {
      setDesignerRates({});
      return;
    }
    const unsub = onSnapshot(
      collection(db, 'userCompensation'),
      (snap) => {
        const next: Record<string, number> = {};
        snap.docs.forEach((row) => {
          const rate = compensationRate(row.data() as Record<string, unknown>);
          if (rate != null) next[row.id] = rate;
        });
        setDesignerRates(next);
      },
      (err) => {
        setDesignerRates({});
        firestoreSnapshotListenError('useJoineryPeople', 'userCompensation', err);
      },
    );
    return unsub;
  }, [canViewRates]);

  return useMemo(
    () => ({
      staff: staffToJoineryPeople(installPeople),
      designers: designersToJoineryPeople(crmUsers, canViewRates ? designerRates : {}),
    }),
    [canViewRates, crmUsers, designerRates, installPeople],
  );
}

export function useAssignedProjectDesigner(): { uid: string; name: string } {
  const { dealId } = useParams<{ dealId?: string }>();
  const { crmUsers, enhancedDeals } = useCRM();
  return useMemo(() => {
    const deal = dealId ? enhancedDeals.find((row) => row.id === dealId) : undefined;
    return getAssignedDesigner(deal, crmUsers);
  }, [dealId, enhancedDeals, crmUsers]);
}
